import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { apiClient } from "~/lib/client";
import {
  LoginInput,
  CreateAdminInput,
} from "~/types/auth";

import { authService } from "../service/auth.service";
import { AdminLoginData, AdminLoginResponse, CreateAdminData, CreateAdminResponse, ResetErrorMessage, ResetPasswordType, ResetSuccessMessage, UpdatePasswordType } from "../types";
import { handleError, handleResponse } from "~/utils/response";


export const useCreateAdminMutation = () => {
  return useMutation<CreateAdminResponse, unknown, CreateAdminInput>({
    mutationFn: async (userData: CreateAdminInput) => {
      try {
        const response = await apiClient("/admin/create-admin", {
          method: "POST",
          body: JSON.stringify(userData),
        });
        return await handleResponse<CreateAdminData>(response);
      } catch (error) {
        return handleError(error);
      }
    },
  });
};

export const useLoginMutation = () => {
  return useMutation<AdminLoginResponse, unknown, LoginInput>({
    mutationFn: async (userData: LoginInput) => {
      try {
        const response = await apiClient("/admin/login", {
          method: "POST",
          body: JSON.stringify(userData),
        });
        return await handleResponse<AdminLoginData>(response);
      } catch (error) {
        return handleError(error);
      }
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      await apiClient("/admin/logout", { method: "POST" });
      await authService.logout();
    },
    onSuccess: () => {
      queryClient.clear();
      navigate({ to: "/", search: { redirect: "/dashboard" } });
      toast.success("Successfully logged out!");
    },
  });
};

export const useUpdatePasswordMutation = () => {
  return useMutation({
    mutationFn: async (data: UpdatePasswordType) => {
      const response = await apiClient("/admin/update-password", {
        method: "PUT",
        body: JSON.stringify({
          token: data.token,
          password: data.password,
        }),
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        return {
          details: {
            message: responseData.error || "Failed to update password"
          }
        };
      }

      return responseData;
    }
  });
};

export const useResetPasswordMutation = ()=> {
  return useMutation<ResetSuccessMessage, ResetErrorMessage, ResetPasswordType>({
    mutationFn: async (data: ResetPasswordType)=> {
      const response = await apiClient("/admin/forgot-password", {
        method: "POST",
        body: JSON.stringify({
          email: data.email,
          reset_link: "https://proxymedicinedash.pages.dev/update-password"
        }),
      })

      const responseData = await response.json();
      
      if (!response.ok) {
        return {
          details: {
            message: responseData.error || "Failed to reset password"
          }
        };
      }

      return responseData;
    }
  });
}
